import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {Select} from 'react-select-virtualized'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import CancelIcon from '@mui/icons-material/Cancel'
import {useTheme} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import {emphasize} from '@mui/material/styles'
import {Tooltip} from '@mui/material'
import {OcTheme, syskronPalette} from '@oc/styles'
import {ControlProps, MultiValueProps, PlaceholderProps, StylesConfig, CSSObjectWithLabel} from 'react-select'
import {uuidv4} from '@oc/utils'
import {cx} from '@emotion/css'

const useStyles = makeOcStyles(theme => ({
    root: {
        width: '100%'
    },
    input: {
        display: 'flex',
        padding: '13px',
        alignItems: 'center'
    },
    chip: {
        margin: '3px',
        maxWidth: 'calc( 100% - 6px)'
    },
    chipFocused: {
        backgroundColor: emphasize(
            theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
            0.08
        )
    },
    helper: {
        flex: '1 1 100%',
        paddingLeft: '13px'
    }
}))

export interface OCSelectOptionType<T = string | number> {
    label: string
    value: T
}

interface ValidationResult {
    message?: string
    error: boolean
}

interface OcAutocompleteSelectPropTypes<IsMulti extends boolean = false, SuggestionValue = string | number> {
    placeholder?: string
    isClearable?: boolean
    suggestions: OCSelectOptionType<SuggestionValue>[]
    label: string
    value?: IsMulti extends true ? OCSelectOptionType<SuggestionValue>[] : OCSelectOptionType<SuggestionValue>
    onChange: (
        data: IsMulti extends true ? OCSelectOptionType<SuggestionValue>[] : OCSelectOptionType<SuggestionValue>
    ) => void
    hideHelper?: boolean
    helper?: string
    disabled?: boolean
    multi?: IsMulti
    rootId: string
    onMenuOpen?: () => void
    onMenuClose?: () => void
    onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void
    onInputChange?: (_: any, newValue: string | null) => void
    mandatory?: boolean
    menuPopUp?: boolean
    menuPlacement?: 'bottom' | 'top' | 'auto'
    noOptionsMessage?: string
    showTooltip?: boolean
    validator?: (
        value?: IsMulti extends true ? OCSelectOptionType<SuggestionValue>[] : OCSelectOptionType<SuggestionValue>
    ) => ValidationResult
    multiSearch?: boolean
    errorMessage?: string
    helperLinkCustomComponent?: JSX.Element
}

function isValueEmpty<T>(valueToCheck?: OCSelectOptionType<T> | OCSelectOptionType<T>[] | null) {
    if (
        !valueToCheck ||
        (Array.isArray(valueToCheck) && !valueToCheck.length) ||
        (!Array.isArray(valueToCheck) && !valueToCheck.value)
    ) {
        return true
    }
    return false
}

const inputComponent = React.forwardRef<HTMLDivElement, any>(function InputComponent({isMulti, ...props}, ref) {
    return <div ref={ref} {...props} style={isMulti ? {height: 'min-content', padding: '5px 13px 4px 13px'} : {}} />
})

function Control(props: ControlProps<OCSelectOptionType, boolean>) {
    const label = React.useMemo(() => {
        if (isValueEmpty((props.selectProps as any).value) && !props.isFocused) {
            return ''
        }
        return (props.selectProps as any).label
    }, [props.isFocused, props.selectProps])

    return (
        <TextField
            fullWidth
            variant="outlined"
            id={(props.selectProps as any).rootId}
            label={label}
            error={
                ((props.selectProps as any).validationResult.error || (props.selectProps as any).errorMessage) &&
                !props.isFocused
            }
            disabled={props.isDisabled}
            slotProps={{
                input: {
                    inputComponent,
                    inputProps: {
                        ref: props.innerRef,
                        children: props.children,
                        isMulti: props.isMulti,
                        ...(props.innerProps as any)
                    },
                    classes: {
                        input: (props.selectProps as any).classes.input
                    }
                },

                inputLabel: {
                    shrink: true,
                    error: (props.selectProps as any).validationResult.error || (props.selectProps as any).errorMessage
                }
            }}
        />
    )
}

function Placeholder(props: PlaceholderProps<OCSelectOptionType, boolean>) {
    const placeholderProps: {color: string; children: React.ReactNode} = React.useMemo(() => {
        if (isValueEmpty((props.selectProps as any).value) && props.isFocused) {
            return {
                children: props.children,
                color: syskronPalette.colors.P200
            }
        }
        return {
            children: (props.selectProps as any).label,
            color: syskronPalette.common.textGrey
        }
    }, [props.selectProps, props.isFocused, props.children])

    return (
        <Typography {...(props.innerProps as any)} style={{color: placeholderProps.color}}>
            {placeholderProps.children}
        </Typography>
    )
}

function MultiValue(props: MultiValueProps<OCSelectOptionType>) {
    return (
        <Chip
            tabIndex={-1}
            label={props.children}
            className={cx((props.selectProps as any).classes.chip, {
                [(props.selectProps as any).classes.chipFocused]: props.isFocused
            })}
            onDelete={props.removeProps.onClick}
            deleteIcon={<CancelIcon {...(props.removeProps as any)} />}
            size="small"
        />
    )
}

const components = {
    Control,
    MultiValue,
    Placeholder
}

function _OcAutocompleteSelect<IsMulti extends boolean = false, SuggestionValue = string | number>(
    props: OcAutocompleteSelectPropTypes<IsMulti, SuggestionValue>
) {
    const {classes} = useStyles()
    const theme = useTheme<OcTheme>()
    const {value, validator} = props
    const [componentId] = React.useState(uuidv4())

    const selectStyles: StylesConfig = React.useMemo(() => {
        const menuItemStyle: CSSObjectWithLabel = {
            '& .fast-option': {
                fontFamily: 'Noto Sans',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: syskronPalette.common.textBlue
            },
            '& .fast-option-focused': {
                backgroundColor: syskronPalette.common.itemHover
            },
            '& .fast-option-selected': {
                backgroundColor: syskronPalette.common.itemSelected
            }
        }

        return {
            input: (base: CSSObjectWithLabel): CSSObjectWithLabel => {
                return {
                    ...base,
                    margin: 0,
                    padding: 0,
                    width: 0,
                    color: theme.syskronPalette.common.textBlue,
                    '&::selection': {
                        color: syskronPalette.common.textBlue,
                        backgroundColor: syskronPalette.colors.P200
                    }
                }
            },
            indicatorSeparator: (base: CSSObjectWithLabel): CSSObjectWithLabel => {
                return {
                    ...base,
                    height: '100%',
                    alignSelf: 'center'
                }
            },
            container: (base: CSSObjectWithLabel): CSSObjectWithLabel => {
                return {
                    ...base
                }
            },
            valueContainer: (base: CSSObjectWithLabel): CSSObjectWithLabel => {
                return {
                    ...base,
                    display: 'flex',
                    padding: 0
                }
            },
            singleValue: (base: CSSObjectWithLabel): CSSObjectWithLabel => {
                return {
                    ...base,
                    fontSize: 16,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 'calc(100% - 10px)',
                    color: syskronPalette.common.textBlue
                }
            },
            menu: (base: CSSObjectWithLabel): CSSObjectWithLabel => {
                return {
                    ...base,
                    borderRadius: 0,
                    zIndex: 100,
                    width: '100%',
                    ...menuItemStyle
                }
            },
            menuPortal: (base: CSSObjectWithLabel): CSSObjectWithLabel => {
                return {
                    ...base,
                    zIndex: 9999,
                    ...menuItemStyle
                }
            }
        }
    }, [theme])

    const label: string = React.useMemo(() => {
        if (props.mandatory === true) {
            return `${props.label} *`
        }
        return props.label
    }, [props.label, props.mandatory])

    const validationResult: ValidationResult = React.useMemo(() => {
        if (validator) {
            return validator(value)
        }

        return {
            error: false
        }
    }, [value, validator])

    const customFilter = React.useCallback(
        (option: OCSelectOptionType<SuggestionValue>, searchText: string): boolean => {
            const optionLabel = option.label.toLowerCase()
            return searchText
                .toLowerCase()
                .split(' ')
                .every(word => optionLabel.includes(word))
        },
        []
    )

    const target = React.useMemo(() => {
        // If menuPopUp is set the menu is moved to (position of the Select + position of the "target")
        //   - the menu has "x" and "y" set to position of the Select relative in the viewport
        //   - the menu is placed as a child of "target", inheriting its position
        //
        // This is necessary to make OcAutocompleteSelect work as cell renderer in AgGrid (for example in component-periodic-operations)
        return props.menuPopUp ? document.body : document.getElementById(componentId)
    }, [props.menuPopUp, componentId])

    const helperText = props.errorMessage || validationResult.message || props.helper

    const selectedValues = Array.isArray(props.value)
        ? props.value.map(x => x.value)
        : props.value
        ? [props.value.value]
        : []

    return (
        <>
            <Tooltip title={props.showTooltip ? label || '' : ''}>
                <div
                    className={classes.root}
                    data-cy={`${props.rootId}-root-select`}
                    data-selected-value={selectedValues.join(',')}
                >
                    <Select
                        id={componentId}
                        className={classes.root}
                        classes={classes}
                        styles={{...selectStyles}}
                        options={props.suggestions}
                        components={components}
                        value={isValueEmpty(props.value) ? [] : props.value}
                        isDisabled={props.disabled || false}
                        rootId={props.rootId}
                        label={label}
                        helperText={props.helper}
                        validationResult={validationResult}
                        onChange={props.onChange}
                        placeholder={props.placeholder || ''}
                        isClearable={props.isClearable !== undefined ? props.isClearable : false}
                        isMulti={props.multi}
                        closeMenuOnSelect={!props.multi}
                        onMenuClose={props.onMenuClose}
                        onMenuOpen={props.onMenuOpen}
                        onKeyDown={props.onKeyDown}
                        onInputChange={props.onInputChange}
                        menuPosition={props.menuPopUp === true ? 'fixed' : 'absolute'}
                        menuPlacement={props.menuPlacement ? props.menuPlacement : 'auto'}
                        menuPortalTarget={target}
                        menuShouldBlockScroll={props.menuPopUp ? true : false}
                        noOptionsMessage={() => props.noOptionsMessage || 'No options'}
                        filterOption={props.multiSearch ? customFilter : undefined}
                        errorMessage={!!props.errorMessage}
                    />
                </div>
            </Tooltip>
            {!props.hideHelper ? (
                <div>
                    <Typography
                        className={classes.helper}
                        color={validationResult.error || props.errorMessage ? 'error' : 'textPrimary'}
                        variant="caption"
                        id={`${props.rootId}-helper-text`}
                    >
                        {helperText || '\xA0'}
                        {helperText && helperText !== '' && props.helperLinkCustomComponent ? '\xA0 - \xA0' : ''}
                    </Typography>
                    {props.helperLinkCustomComponent ? props.helperLinkCustomComponent : null}
                </div>
            ) : null}
        </>
    )
}

export const OcAutocompleteSelect = observer(_OcAutocompleteSelect)
export default OcAutocompleteSelect
